import { Container, Graphics, BlurFilter, Text, TextStyle } from 'pixi.js';
import { WORLD_RADIUS, MapShape, ShrinkingPhase } from '@wooorms/shared';
import { UIScaleManager } from '../utils/UIScaleManager';

/**
 * Worm data for minimap display
 */
export interface MinimapWorm {
  id: string;
  x: number;
  y: number;
  color: number;
  isLocal: boolean;
  /** Whether this is LEVIATHAN (legendary AI) */
  isLegendary?: boolean;
  /** Name for LEVIATHAN label */
  name?: string;
}

/**
 * Food data for minimap display (used for feast detection)
 */
export interface MinimapFood {
  x: number;
  y: number;
  value?: number;
}

/**
 * Feast tier definitions
 */
export enum FeastTier {
  NONE = 'none',
  FEAST = 'feast',        // 50+ value
  BANQUET = 'banquet',    // 100+ value
  GOLDEN_BUFFET = 'golden_buffet',  // 250+ value
  MYTHICAL_FEAST = 'mythical_feast', // 500+ value
}

/**
 * Feast ping data
 */
export interface FeastPing {
  id: string;
  x: number;
  y: number;
  tier: FeastTier;
  value: number;
  spawnTime: number;
  duration: number;
  zone: string;
}

/**
 * Hot zone status levels
 */
export enum HotZoneStatus {
  NONE = 'none',
  SNACK_ZONE = 'snack',     // 50+ food value
  FEAST_ZONE = 'feast',     // 150+ food value
  HOT_ZONE = 'hot',         // 300+ food value
  JACKPOT_ZONE = 'jackpot', // 500+ food value
}

/**
 * Hot zone data
 */
export interface HotZone {
  zone: string;
  status: HotZoneStatus;
  totalValue: number;
  centerX: number;
  centerY: number;
}

/**
 * Event ticker message
 */
export interface TickerMessage {
  text: string;
  color: number;
  timestamp: number;
  duration: number;
}

/**
 * Shrinking zone state for minimap visualization
 */
export interface ShrinkingZoneState {
  enabled: boolean;
  currentBounds: { x: number; y: number; width: number; height: number };
  targetBounds: { x: number; y: number; width: number; height: number };
  phase: ShrinkingPhase;
  warningTimer: number;
}

/**
 * Minimap configuration
 */
export interface MinimapConfig {
  /** Size as percentage of screen (0 to 1) */
  sizePercent: number;
  /** Background alpha */
  backgroundAlpha: number;
  /** Border pulse speed */
  pulseSpeed: number;
  /** World radius for scaling (circular maps) */
  worldRadius: number;
  /** Map shape (circular or rectangular) */
  mapShape: MapShape;
  /** World width for rectangular maps */
  worldWidth?: number;
  /** World height for rectangular maps */
  worldHeight?: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: MinimapConfig = {
  sizePercent: 0.18,
  backgroundAlpha: 0.85,
  pulseSpeed: 3,
  worldRadius: WORLD_RADIUS,
  mapShape: MapShape.CIRCULAR,
};

/**
 * Feast tier value thresholds
 */
const FEAST_TIER_THRESHOLDS = {
  FEAST: 50,
  BANQUET: 100,
  GOLDEN_BUFFET: 250,
  MYTHICAL_FEAST: 500,
} as const;

/**
 * Feast tier configurations
 */
const FEAST_CONFIGS = {
  [FeastTier.FEAST]: {
    minValue: FEAST_TIER_THRESHOLDS.FEAST,
    color: 0xffff00,      // Yellow
    duration: 3,
    announcement: 'FEAST spotted!',
    pulseSpeed: 2,
    size: 8,
  },
  [FeastTier.BANQUET]: {
    minValue: FEAST_TIER_THRESHOLDS.BANQUET,
    color: 0xff8800,      // Orange
    duration: 8,
    announcement: 'BANQUET at [ZONE]!',
    pulseSpeed: 3,
    size: 10,
  },
  [FeastTier.GOLDEN_BUFFET]: {
    minValue: FEAST_TIER_THRESHOLDS.GOLDEN_BUFFET,
    color: 0xff0000,      // Red
    duration: 15,
    announcement: 'GOLDEN BUFFET at [ZONE]!',
    pulseSpeed: 5,
    size: 12,
  },
  [FeastTier.MYTHICAL_FEAST]: {
    minValue: FEAST_TIER_THRESHOLDS.MYTHICAL_FEAST,
    color: 0xff00ff,      // Magenta/Crown
    duration: 30,
    announcement: 'MYTHICAL FEAST at [ZONE]!!!',
    pulseSpeed: 8,
    size: 16,
  },
} as const;

/**
 * Hot zone value thresholds
 */
const HOT_ZONE_THRESHOLDS = {
  SNACK: 50,
  FEAST: 150,
  HOT: 300,
  JACKPOT: 500,
} as const;

/**
 * Hot zone configurations
 */
const HOT_ZONE_CONFIGS = {
  [HotZoneStatus.SNACK_ZONE]: {
    minValue: HOT_ZONE_THRESHOLDS.SNACK,
    color: 0x00ff00,      // Green tint
    alpha: 0.15,
  },
  [HotZoneStatus.FEAST_ZONE]: {
    minValue: HOT_ZONE_THRESHOLDS.FEAST,
    color: 0xffff00,      // Yellow glow
    alpha: 0.25,
  },
  [HotZoneStatus.HOT_ZONE]: {
    minValue: HOT_ZONE_THRESHOLDS.HOT,
    color: 0xff4400,      // Red pulse
    alpha: 0.35,
    showText: true,
  },
  [HotZoneStatus.JACKPOT_ZONE]: {
    minValue: HOT_ZONE_THRESHOLDS.JACKPOT,
    color: 0xffd700,      // Crown/Gold
    alpha: 0.45,
    showCrown: true,
  },
} as const;

/**
 * Retro arcade color palette
 */
const MINIMAP_COLORS = {
  background: 0x0a0a1a,
  backgroundRing: 0x151528,
  border: 0x4ecdc4,      // Teal border (matches game theme)
  borderGlow: 0x00ffcc,  // Bright cyan glow
  gridLines: 0x1a1a2e,   // Subtle grid
  playerGlow: 0xffd700,  // Golden glow
  playerCore: 0xffff00,  // Bright yellow core
  dangerZone: 0xff4444,  // Red danger ring
  tickerBg: 0x0a0a1a,    // Ticker background
  tickerText: 0xffffff,  // Ticker text
  // LEVIATHAN colors
  leviathanPrimary: 0x4a0080,   // Dark purple
  leviathanSecondary: 0xffd700, // Gold
  leviathanGlow: 0x6a0dad,      // Purple glow
  // Shrinking zone colors
  shrinkingDanger: 0xff2222,    // Red danger overlay
  shrinkingSafe: 0x00ff88,      // Green safe border
  shrinkingTarget: 0xff4444,    // Red target zone preview
  shrinkingWarning: 0xffaa00,   // Orange warning
} as const;

/**
 * Compass directions for zones
 */
const COMPASS_DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/**
 * Minimap - Supports both circular and rectangular world shapes with shrinking zone visualization
 *
 * Features:
 * - Circular or rectangular shape matching world
 * - Shows AI worms as colored dots
 * - Shows local player as larger golden dot with white border
 * - Pulsing border matching world edge
 * - Semi-transparent dark background
 * - Positioned at top-right corner
 * - Feast ping system for large food clusters
 * - Hot zone indicators for food-dense areas
 * - Event ticker for announcements
 * - Zone-based coordinate system
 * - Shrinking zone visualization for battle royale mode
 *
 * Note: Regular food is NOT shown - only significant feast events!
 */
export class Minimap {
  /** Main container */
  public container: Container;

  /** Configuration */
  private config: MinimapConfig;

  /** Background graphics */
  private background: Graphics;

  /** Border graphics */
  private border: Graphics;

  /** Border glow for pulsing effect */
  private borderGlow: Graphics;

  /** Content graphics (worms) */
  private content: Graphics;

  /** Feast pings graphics */
  private feastPings: Graphics;

  /** Hot zones graphics */
  private hotZones: Graphics;

  /** Player marker graphics */
  private playerMarker: Graphics;

  /** Shrinking zone danger overlay */
  private shrinkingZoneGraphics: Graphics;

  /** Safe zone border */
  private safeZoneBorder: Graphics;

  /** Zone label text */
  private zoneLabel: Text;

  /** Event ticker container */
  private tickerContainer: Container;

  /** Ticker background graphics */
  private tickerBg: Graphics;

  /** Ticker text */
  private tickerText: Text;

  /** Current minimap radius in pixels (for circular) */
  private minimapRadius: number = 100;

  /** Current minimap width in pixels (for rectangular) */
  private minimapWidth: number = 200;

  /** Current minimap height in pixels (for rectangular) */
  private minimapHeight: number = 200;

  /** Animation time */
  private animTime: number = 0;

  /** Blur filter for glow */
  private glowFilter: BlurFilter;

  /** Whether minimap is visible */
  private visible: boolean = false;

  /** Active feast pings */
  private activePings: Map<string, FeastPing> = new Map();

  /** Active hot zones */
  private activeHotZones: Map<string, HotZone> = new Map();

  /** Ticker message queue */
  private tickerMessages: TickerMessage[] = [];

  /** Current player zone */
  private currentZone: string = 'CENTER';

  /** Ping ID counter */
  private pingIdCounter: number = 0;

  /** Last processed food hash (to avoid duplicate processing) */
  private lastFoodHash: string = '';

  /** Current shrinking zone state */
  private shrinkingZone: ShrinkingZoneState = {
    enabled: false,
    currentBounds: { x: 0, y: 0, width: 0, height: 0 },
    targetBounds: { x: 0, y: 0, width: 0, height: 0 },
    phase: ShrinkingPhase.WAITING,
    warningTimer: 0,
  };

  /** Screen dimensions for resize calculations */
  private screenWidth: number = 0;
  private screenHeight: number = 0;

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  /** Whether the minimap is minimized */
  private minimized: boolean = false;

  /** DOM minimize button */
  private minimizeBtn: HTMLButtonElement | null = null;

  constructor(config: Partial<MinimapConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();

    // Create container
    this.container = new Container();
    this.container.label = 'minimap';

    // Create graphics layers
    this.background = new Graphics();
    this.borderGlow = new Graphics();
    this.shrinkingZoneGraphics = new Graphics();
    this.safeZoneBorder = new Graphics();
    this.hotZones = new Graphics();
    this.border = new Graphics();
    this.feastPings = new Graphics();
    this.content = new Graphics();
    this.playerMarker = new Graphics();

    // Setup blur filter for glow
    this.glowFilter = new BlurFilter({
      strength: 6,
      quality: 3,
    });
    this.borderGlow.filters = [this.glowFilter];

    // Create zone label
    const zoneLabelStyle = new TextStyle({
      fontFamily: 'monospace',
      fontSize: this.scaleManager.scaleFontSize(10),
      fill: 0x88ccff,
      fontWeight: 'bold',
    });
    this.zoneLabel = new Text({ text: '', style: zoneLabelStyle });
    this.zoneLabel.anchor.set(1, 1);

    // Create ticker container
    this.tickerContainer = new Container();
    this.tickerBg = new Graphics();
    const tickerStyle = new TextStyle({
      fontFamily: 'monospace',
      fontSize: this.scaleManager.scaleFontSize(10),
      fill: MINIMAP_COLORS.tickerText,
      fontWeight: 'bold',
    });
    this.tickerText = new Text({ text: '', style: tickerStyle });
    this.tickerText.anchor.set(0.5, 0.5);
    this.tickerContainer.addChild(this.tickerBg);
    this.tickerContainer.addChild(this.tickerText);

    // Add layers in order
    this.container.addChild(this.background);
    this.container.addChild(this.borderGlow);
    this.container.addChild(this.shrinkingZoneGraphics);
    this.container.addChild(this.safeZoneBorder);
    this.container.addChild(this.hotZones);
    this.container.addChild(this.border);
    this.container.addChild(this.feastPings);
    this.container.addChild(this.content);
    this.container.addChild(this.playerMarker);
    this.container.addChild(this.zoneLabel);
    this.container.addChild(this.tickerContainer);

    // Initially hidden
    this.container.visible = false;

    // Listen for scale changes
    this.scaleManager.addListener(() => this.applyScaling());

    // Create DOM minimize button (positioned in onResize)
    this.createMinimizeButton();
  }

  /**
   * Create a DOM-based minimize button for the minimap
   */
  private createMinimizeButton(): void {
    this.minimizeBtn = document.createElement('button');
    this.minimizeBtn.innerHTML = '−';
    this.minimizeBtn.style.cssText = `
      position: fixed;
      top: 15px;
      right: 15px;
      width: 24px;
      height: 24px;
      background: rgba(15, 15, 35, 0.9);
      border: 1px solid rgba(78, 205, 196, 0.5);
      border-radius: 4px;
      color: #4ecdc4;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      z-index: 101;
      display: none;
      line-height: 1;
      transition: opacity 0.2s, background 0.2s;
    `;
    this.minimizeBtn.addEventListener('click', () => this.toggleMinimize());
    this.minimizeBtn.addEventListener('mouseenter', () => {
      if (this.minimizeBtn) this.minimizeBtn.style.background = 'rgba(78, 205, 196, 0.3)';
    });
    this.minimizeBtn.addEventListener('mouseleave', () => {
      if (this.minimizeBtn) this.minimizeBtn.style.background = 'rgba(15, 15, 35, 0.9)';
    });
    document.body.appendChild(this.minimizeBtn);
  }

  /**
   * Apply current scaling to all text elements
   */
  private applyScaling(): void {
    (this.zoneLabel.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(10);
    (this.tickerText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(10);
    this.drawTickerBackground();
  }

  /**
   * Show the minimap
   */
  show(): void {
    this.visible = true;
    this.container.visible = !this.minimized;
    if (this.minimizeBtn) {
      this.minimizeBtn.style.display = 'block';
    }
  }

  /**
   * Hide the minimap
   */
  hide(): void {
    this.visible = false;
    this.container.visible = false;
    if (this.minimizeBtn) {
      this.minimizeBtn.style.display = 'none';
    }
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Toggle the minimized state of the minimap
   */
  toggleMinimize(): void {
    this.minimized = !this.minimized;

    if (this.visible) {
      this.container.visible = !this.minimized;
    }

    if (this.minimizeBtn) {
      this.minimizeBtn.innerHTML = this.minimized ? '+' : '−';
    }
  }

  /**
   * Set the map shape and optional dimensions
   */
  setMapShape(shape: MapShape, width?: number, height?: number): void {
    this.config.mapShape = shape;
    if (width !== undefined) {
      this.config.worldWidth = width;
    }
    if (height !== undefined) {
      this.config.worldHeight = height;
    }

    // Recalculate minimap size if we have screen dimensions
    if (this.screenWidth > 0 && this.screenHeight > 0) {
      this.onResize(this.screenWidth, this.screenHeight);
    }
  }

  /**
   * Update shrinking zone state
   */
  updateShrinkingZone(state: ShrinkingZoneState): void {
    this.shrinkingZone = state;
  }

  /**
   * Handle screen resize
   */
  onResize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    const minDimension = Math.min(width, height);

    if (this.config.mapShape === MapShape.RECTANGULAR) {
      // For rectangular maps, maintain aspect ratio
      const worldWidth = this.config.worldWidth ?? this.config.worldRadius * 2;
      const worldHeight = this.config.worldHeight ?? this.config.worldRadius * 2;
      const aspectRatio = worldWidth / worldHeight;

      // Calculate minimap size based on screen and aspect ratio
      const baseSize = minDimension * this.config.sizePercent;

      if (aspectRatio >= 1) {
        // Wider than tall
        this.minimapWidth = baseSize;
        this.minimapHeight = baseSize / aspectRatio;
      } else {
        // Taller than wide
        this.minimapHeight = baseSize;
        this.minimapWidth = baseSize * aspectRatio;
      }

      // Also set radius for backward compatibility with some methods
      this.minimapRadius = Math.min(this.minimapWidth, this.minimapHeight) / 2;

      // Position at top-right corner with padding
      const padding = 15;
      this.container.position.set(
        width - this.minimapWidth / 2 - padding,
        this.minimapHeight / 2 + padding
      );

      // Position zone label at bottom-right of minimap
      this.zoneLabel.position.set(
        this.minimapWidth / 2 - 5,
        this.minimapHeight / 2 - 5
      );

      // Position ticker below minimap
      this.tickerContainer.position.set(0, this.minimapHeight / 2 + 12);
    } else {
      // Circular map - original logic
      this.minimapRadius = minDimension * this.config.sizePercent * 0.5;
      this.minimapWidth = this.minimapRadius * 2;
      this.minimapHeight = this.minimapRadius * 2;

      // Position at top-right corner with padding
      const padding = 15;
      this.container.position.set(
        width - this.minimapRadius - padding,
        this.minimapRadius + padding
      );

      // Position zone label at bottom-right of minimap
      this.zoneLabel.position.set(
        this.minimapRadius - 5,
        this.minimapRadius - 5
      );

      // Position ticker below minimap
      this.tickerContainer.position.set(0, this.minimapRadius + 12);
    }

    // Redraw background and border
    this.drawBackground();
    this.drawBorder();
    this.drawTickerBackground();
  }

  /**
   * Get zone name from world coordinates
   */
  getZoneName(worldX: number, worldY: number): string {
    const worldRadius = this.config.worldRadius;

    // Calculate distance from center
    const distance = Math.sqrt(worldX * worldX + worldY * worldY);
    const distanceRatio = distance / worldRadius;

    // Determine ring (CORE/MID/RIM)
    let ring: string;
    if (distanceRatio < 0.33) {
      ring = 'CORE';
    } else if (distanceRatio < 0.66) {
      ring = 'MID';
    } else {
      ring = 'RIM';
    }

    // If very close to center, just return CENTER
    if (distanceRatio < 0.1) {
      return 'CENTER';
    }

    // Calculate angle for compass direction
    let angle = Math.atan2(worldY, worldX) * (180 / Math.PI);
    // Convert to 0-360 range (atan2 returns -180 to 180)
    angle = (angle + 360) % 360;
    // Adjust so north is 0 (atan2 has east at 0)
    angle = (angle + 90) % 360;

    // Determine compass direction (8 directions, 45 degrees each)
    const directionIndex = Math.floor((angle + 22.5) / 45) % 8;
    const direction = COMPASS_DIRECTIONS[directionIndex];

    return `${direction}-${ring}`;
  }

  /**
   * Get feast tier from food value
   */
  private getFeastTier(value: number): FeastTier {
    if (value >= FEAST_TIER_THRESHOLDS.MYTHICAL_FEAST) return FeastTier.MYTHICAL_FEAST;
    if (value >= FEAST_TIER_THRESHOLDS.GOLDEN_BUFFET) return FeastTier.GOLDEN_BUFFET;
    if (value >= FEAST_TIER_THRESHOLDS.BANQUET) return FeastTier.BANQUET;
    if (value >= FEAST_TIER_THRESHOLDS.FEAST) return FeastTier.FEAST;
    return FeastTier.NONE;
  }

  /**
   * Get hot zone status from food value
   */
  private getHotZoneStatus(value: number): HotZoneStatus {
    if (value >= HOT_ZONE_THRESHOLDS.JACKPOT) return HotZoneStatus.JACKPOT_ZONE;
    if (value >= HOT_ZONE_THRESHOLDS.HOT) return HotZoneStatus.HOT_ZONE;
    if (value >= HOT_ZONE_THRESHOLDS.FEAST) return HotZoneStatus.FEAST_ZONE;
    if (value >= HOT_ZONE_THRESHOLDS.SNACK) return HotZoneStatus.SNACK_ZONE;
    return HotZoneStatus.NONE;
  }

  /**
   * Add a feast ping
   */
  addFeastPing(x: number, y: number, value: number): void {
    const tier = this.getFeastTier(value);
    if (tier === FeastTier.NONE) return;

    // Only tiers with configs (FEAST, BANQUET, GOLDEN_BUFFET, MYTHICAL_FEAST) get pings
    if (!(tier in FEAST_CONFIGS)) return;

    const config = FEAST_CONFIGS[tier as keyof typeof FEAST_CONFIGS];
    const zone = this.getZoneName(x, y);
    const id = `feast-${this.pingIdCounter++}`;

    const ping: FeastPing = {
      id,
      x,
      y,
      tier,
      value,
      spawnTime: this.animTime,
      duration: config.duration,
      zone,
    };

    this.activePings.set(id, ping);

    // Add ticker announcement
    const announcement = config.announcement.replace('[ZONE]', zone);
    this.addTickerMessage(announcement, config.color, config.duration);
  }

  /**
   * Add ticker message
   */
  addTickerMessage(text: string, color: number, duration: number = 5): void {
    this.tickerMessages.push({
      text,
      color,
      timestamp: this.animTime,
      duration,
    });

    // Keep only last 3 messages
    if (this.tickerMessages.length > 3) {
      this.tickerMessages.shift();
    }
  }

  /**
   * Process food data to detect feasts and hot zones
   */
  private processFoodData(food: MinimapFood[]): void {
    // Create a simple hash to check if food changed
    const foodHash = `${food.length}-${food.slice(0, 10).map(f => `${Math.round(f.x)},${Math.round(f.y)}`).join(';')}`;
    if (foodHash === this.lastFoodHash) return;
    this.lastFoodHash = foodHash;

    // Clear existing hot zones
    this.activeHotZones.clear();

    // Group food by zones
    const zoneFood = new Map<string, { totalValue: number; foods: MinimapFood[]; sumX: number; sumY: number }>();

    for (const f of food) {
      const zone = this.getZoneName(f.x, f.y);
      const value = f.value ?? 1;

      if (!zoneFood.has(zone)) {
        zoneFood.set(zone, { totalValue: 0, foods: [], sumX: 0, sumY: 0 });
      }

      const zoneData = zoneFood.get(zone)!;
      zoneData.totalValue += value;
      zoneData.foods.push(f);
      zoneData.sumX += f.x;
      zoneData.sumY += f.y;
    }

    // Check each zone for hot zone status
    for (const [zone, data] of zoneFood) {
      const status = this.getHotZoneStatus(data.totalValue);
      if (status !== HotZoneStatus.NONE) {
        const centerX = data.sumX / data.foods.length;
        const centerY = data.sumY / data.foods.length;

        this.activeHotZones.set(zone, {
          zone,
          status,
          totalValue: data.totalValue,
          centerX,
          centerY,
        });
      }

      // Check for feast-worthy clusters within zone
      // Look for concentrated food clusters
      const tier = this.getFeastTier(data.totalValue);
      if (tier !== FeastTier.NONE) {
        // Check if we already have a ping near this location
        const centerX = data.sumX / data.foods.length;
        const centerY = data.sumY / data.foods.length;
        let hasNearbyPing = false;

        for (const ping of this.activePings.values()) {
          const dx = ping.x - centerX;
          const dy = ping.y - centerY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < this.config.worldRadius * 0.1) {
            hasNearbyPing = true;
            break;
          }
        }

        if (!hasNearbyPing) {
          this.addFeastPing(centerX, centerY, data.totalValue);
        }
      }
    }
  }

  /**
   * Draw the minimap background with retro arcade styling
   */
  private drawBackground(): void {
    const g = this.background;
    g.clear();

    if (this.config.mapShape === MapShape.RECTANGULAR) {
      const hw = this.minimapWidth / 2;
      const hh = this.minimapHeight / 2;
      const cornerRadius = 8;

      // Outer decorative ring (arcade cabinet style)
      g.roundRect(-hw - 4, -hh - 4, this.minimapWidth + 8, this.minimapHeight + 8, cornerRadius + 2);
      g.fill({ color: MINIMAP_COLORS.backgroundRing, alpha: 0.9 });

      // Main rectangular background
      g.roundRect(-hw, -hh, this.minimapWidth, this.minimapHeight, cornerRadius);
      g.fill({ color: MINIMAP_COLORS.background, alpha: this.config.backgroundAlpha });

      // Draw subtle grid lines for retro feel
      const gridCountX = 3;
      const gridCountY = 3;

      // Vertical grid lines
      for (let i = 1; i <= gridCountX; i++) {
        const x = -hw + (this.minimapWidth / (gridCountX + 1)) * i;
        g.moveTo(x, -hh * 0.8);
        g.lineTo(x, hh * 0.8);
        g.stroke({ width: 1, color: MINIMAP_COLORS.gridLines, alpha: 0.4 });
      }

      // Horizontal grid lines
      for (let i = 1; i <= gridCountY; i++) {
        const y = -hh + (this.minimapHeight / (gridCountY + 1)) * i;
        g.moveTo(-hw * 0.8, y);
        g.lineTo(hw * 0.8, y);
        g.stroke({ width: 1, color: MINIMAP_COLORS.gridLines, alpha: 0.4 });
      }

      // Draw crosshair lines
      g.moveTo(-hw * 0.8, 0);
      g.lineTo(hw * 0.8, 0);
      g.stroke({ width: 1, color: MINIMAP_COLORS.gridLines, alpha: 0.3 });
      g.moveTo(0, -hh * 0.8);
      g.lineTo(0, hh * 0.8);
      g.stroke({ width: 1, color: MINIMAP_COLORS.gridLines, alpha: 0.3 });
    } else {
      // Circular - original logic
      // Outer decorative ring (arcade cabinet style)
      g.circle(0, 0, this.minimapRadius + 4);
      g.fill({ color: MINIMAP_COLORS.backgroundRing, alpha: 0.9 });

      // Main circular background
      g.circle(0, 0, this.minimapRadius);
      g.fill({ color: MINIMAP_COLORS.background, alpha: this.config.backgroundAlpha });

      // Draw subtle radar grid lines for retro feel
      const gridCount = 3;
      for (let i = 1; i <= gridCount; i++) {
        const ringRadius = (this.minimapRadius / (gridCount + 1)) * i;
        g.circle(0, 0, ringRadius);
        g.stroke({ width: 1, color: MINIMAP_COLORS.gridLines, alpha: 0.4 });
      }

      // Draw crosshair lines
      g.moveTo(-this.minimapRadius * 0.8, 0);
      g.lineTo(this.minimapRadius * 0.8, 0);
      g.stroke({ width: 1, color: MINIMAP_COLORS.gridLines, alpha: 0.3 });
      g.moveTo(0, -this.minimapRadius * 0.8);
      g.lineTo(0, this.minimapRadius * 0.8);
      g.stroke({ width: 1, color: MINIMAP_COLORS.gridLines, alpha: 0.3 });
    }
  }

  /**
   * Draw the minimap border with arcade styling
   */
  private drawBorder(): void {
    const g = this.border;
    g.clear();

    if (this.config.mapShape === MapShape.RECTANGULAR) {
      const hw = this.minimapWidth / 2;
      const hh = this.minimapHeight / 2;
      const cornerRadius = 8;

      // Inner teal border (main)
      g.roundRect(-hw, -hh, this.minimapWidth, this.minimapHeight, cornerRadius);
      g.stroke({ width: 2, color: MINIMAP_COLORS.border, alpha: 0.9 });

      // Outer decorative border
      g.roundRect(-hw - 4, -hh - 4, this.minimapWidth + 8, this.minimapHeight + 8, cornerRadius + 2);
      g.stroke({ width: 1, color: MINIMAP_COLORS.border, alpha: 0.5 });

      // Corner accents (arcade cabinet style)
      const cornerSize = 8;
      const cornerOffset = 10;
      const corners = [
        { x: -hw + cornerOffset, y: -hh + cornerOffset },
        { x: hw - cornerOffset, y: -hh + cornerOffset },
        { x: -hw + cornerOffset, y: hh - cornerOffset },
        { x: hw - cornerOffset, y: hh - cornerOffset },
      ];
      for (const corner of corners) {
        g.rect(corner.x - cornerSize / 2, corner.y - cornerSize / 2, cornerSize, cornerSize);
        g.stroke({ width: 1, color: MINIMAP_COLORS.border, alpha: 0.6 });
      }
    } else {
      // Circular - original logic
      // Inner teal border (main)
      g.circle(0, 0, this.minimapRadius);
      g.stroke({ width: 2, color: MINIMAP_COLORS.border, alpha: 0.9 });

      // Outer decorative border
      g.circle(0, 0, this.minimapRadius + 4);
      g.stroke({ width: 1, color: MINIMAP_COLORS.border, alpha: 0.5 });

      // Small corner accents (arcade cabinet style)
      const cornerSize = 8;
      const cornerDist = this.minimapRadius * 0.7;
      const corners = [
        { x: -cornerDist, y: -cornerDist },
        { x: cornerDist, y: -cornerDist },
        { x: -cornerDist, y: cornerDist },
        { x: cornerDist, y: cornerDist },
      ];
      for (const corner of corners) {
        g.rect(corner.x - cornerSize / 2, corner.y - cornerSize / 2, cornerSize, cornerSize);
        g.stroke({ width: 1, color: MINIMAP_COLORS.border, alpha: 0.6 });
      }
    }
  }

  /**
   * Draw ticker background
   */
  private drawTickerBackground(): void {
    const g = this.tickerBg;
    g.clear();

    const width = this.config.mapShape === MapShape.RECTANGULAR
      ? this.minimapWidth
      : this.minimapRadius * 2;
    const height = 18;

    g.roundRect(-width / 2, -height / 2, width, height, 4);
    g.fill({ color: MINIMAP_COLORS.tickerBg, alpha: 0.85 });
    g.stroke({ width: 1, color: MINIMAP_COLORS.border, alpha: 0.5 });
  }

  /**
   * Update border glow animation with arcade pulsing
   */
  private updateBorderGlow(): void {
    const g = this.borderGlow;
    g.clear();

    // Main pulsing glow
    const pulse = Math.sin(this.animTime * this.config.pulseSpeed) * 0.3 + 0.5;

    if (this.config.mapShape === MapShape.RECTANGULAR) {
      const hw = this.minimapWidth / 2;
      const hh = this.minimapHeight / 2;
      const cornerRadius = 8;

      // Cyan glow (arcade style)
      g.roundRect(-hw, -hh, this.minimapWidth, this.minimapHeight, cornerRadius);
      g.stroke({ width: 6, color: MINIMAP_COLORS.borderGlow, alpha: pulse * 0.4 });

      // Secondary subtle red glow for danger zone indication
      const dangerPulse = Math.sin(this.animTime * 5) * 0.2 + 0.3;
      g.roundRect(-hw + 2, -hh + 2, this.minimapWidth - 4, this.minimapHeight - 4, cornerRadius - 1);
      g.stroke({ width: 2, color: MINIMAP_COLORS.dangerZone, alpha: dangerPulse * 0.3 });
    } else {
      // Circular - original logic
      // Cyan glow (arcade style)
      g.circle(0, 0, this.minimapRadius);
      g.stroke({ width: 6, color: MINIMAP_COLORS.borderGlow, alpha: pulse * 0.4 });

      // Secondary subtle red glow for danger zone indication
      const dangerPulse = Math.sin(this.animTime * 5) * 0.2 + 0.3;
      g.circle(0, 0, this.minimapRadius * 0.95);
      g.stroke({ width: 2, color: MINIMAP_COLORS.dangerZone, alpha: dangerPulse * 0.3 });
    }
  }

  /**
   * Convert world position to minimap position
   */
  private worldToMinimap(worldX: number, worldY: number): { x: number; y: number } {
    if (this.config.mapShape === MapShape.RECTANGULAR) {
      // For rectangular maps, scale by world dimensions
      const worldWidth = this.config.worldWidth ?? this.config.worldRadius * 2;
      const worldHeight = this.config.worldHeight ?? this.config.worldRadius * 2;

      const scaleX = this.minimapWidth / worldWidth;
      const scaleY = this.minimapHeight / worldHeight;

      return {
        x: worldX * scaleX,
        y: worldY * scaleY,
      };
    } else {
      // Circular - scale by radius
      const scale = this.minimapRadius / this.config.worldRadius;
      return {
        x: worldX * scale,
        y: worldY * scale,
      };
    }
  }

  /**
   * Check if a minimap position is within bounds
   */
  private isWithinMinimapBounds(x: number, y: number, margin: number = 0): boolean {
    if (this.config.mapShape === MapShape.RECTANGULAR) {
      const hw = this.minimapWidth / 2 - margin;
      const hh = this.minimapHeight / 2 - margin;
      return Math.abs(x) <= hw && Math.abs(y) <= hh;
    } else {
      const dist = Math.sqrt(x * x + y * y);
      return dist <= this.minimapRadius - margin;
    }
  }

  /**
   * Draw shrinking zone visualization
   */
  private drawShrinkingZone(): void {
    const gDanger = this.shrinkingZoneGraphics;
    const gSafe = this.safeZoneBorder;
    gDanger.clear();
    gSafe.clear();

    if (!this.shrinkingZone.enabled) return;

    // Only draw for rectangular maps (shrinking mode is rectangular only)
    if (this.config.mapShape !== MapShape.RECTANGULAR) return;

    const worldWidth = this.config.worldWidth ?? this.config.worldRadius * 2;
    const worldHeight = this.config.worldHeight ?? this.config.worldRadius * 2;
    const hw = this.minimapWidth / 2;
    const hh = this.minimapHeight / 2;

    const current = this.shrinkingZone.currentBounds;
    const target = this.shrinkingZone.targetBounds;
    const phase = this.shrinkingZone.phase;

    // Convert current bounds to minimap coordinates
    const currMinimapX = (current.x / worldWidth) * this.minimapWidth;
    const currMinimapY = (current.y / worldHeight) * this.minimapHeight;
    const currMinimapW = (current.width / worldWidth) * this.minimapWidth;
    const currMinimapH = (current.height / worldHeight) * this.minimapHeight;

    // Draw danger zone (area outside current safe zone)
    // Draw the full minimap as danger, then cut out the safe zone
    const dangerAlpha = 0.3 + Math.sin(this.animTime * 4) * 0.1;

    // Top danger area
    if (currMinimapY + hh > -hh) {
      gDanger.rect(-hw, -hh, this.minimapWidth, currMinimapY + hh - currMinimapH / 2 + hh);
      gDanger.fill({ color: MINIMAP_COLORS.shrinkingDanger, alpha: dangerAlpha });
    }

    // Bottom danger area
    const bottomStart = currMinimapY + currMinimapH / 2;
    if (bottomStart < hh) {
      gDanger.rect(-hw, bottomStart, this.minimapWidth, hh - bottomStart);
      gDanger.fill({ color: MINIMAP_COLORS.shrinkingDanger, alpha: dangerAlpha });
    }

    // Left danger area
    const leftEnd = currMinimapX - currMinimapW / 2;
    const topDangerEnd = currMinimapY - currMinimapH / 2;
    if (leftEnd > -hw) {
      gDanger.rect(-hw, topDangerEnd, leftEnd + hw, currMinimapH);
      gDanger.fill({ color: MINIMAP_COLORS.shrinkingDanger, alpha: dangerAlpha });
    }

    // Right danger area
    const rightStart = currMinimapX + currMinimapW / 2;
    if (rightStart < hw) {
      gDanger.rect(rightStart, topDangerEnd, hw - rightStart, currMinimapH);
      gDanger.fill({ color: MINIMAP_COLORS.shrinkingDanger, alpha: dangerAlpha });
    }

    // Draw current safe zone border (animated dashed line)
    const safeColor = phase === ShrinkingPhase.WARNING
      ? MINIMAP_COLORS.shrinkingWarning
      : MINIMAP_COLORS.shrinkingSafe;
    const dashOffset = (this.animTime * 20) % 20;

    // Draw safe zone border with animated dashes
    this.drawDashedRect(
      gSafe,
      currMinimapX - currMinimapW / 2,
      currMinimapY - currMinimapH / 2,
      currMinimapW,
      currMinimapH,
      safeColor,
      2,
      0.9,
      10,
      5,
      dashOffset
    );

    // Draw target zone preview during warning phase
    if (phase === ShrinkingPhase.WARNING) {
      const targetMinimapX = (target.x / worldWidth) * this.minimapWidth;
      const targetMinimapY = (target.y / worldHeight) * this.minimapHeight;
      const targetMinimapW = (target.width / worldWidth) * this.minimapWidth;
      const targetMinimapH = (target.height / worldHeight) * this.minimapHeight;

      const targetPulse = Math.sin(this.animTime * 6) * 0.3 + 0.5;

      // Draw target zone with pulsing dashes
      this.drawDashedRect(
        gSafe,
        targetMinimapX - targetMinimapW / 2,
        targetMinimapY - targetMinimapH / 2,
        targetMinimapW,
        targetMinimapH,
        MINIMAP_COLORS.shrinkingTarget,
        2,
        targetPulse,
        8,
        4,
        -dashOffset // Animate in opposite direction
      );
    }
  }

  /**
   * Draw a dashed rectangle
   */
  private drawDashedRect(
    g: Graphics,
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    lineWidth: number,
    alpha: number,
    dashLength: number,
    gapLength: number,
    offset: number
  ): void {
    // Top edge
    this.drawDashedLine(g, x, y, x + width, y, color, lineWidth, alpha, dashLength, gapLength, offset);
    // Right edge
    this.drawDashedLine(g, x + width, y, x + width, y + height, color, lineWidth, alpha, dashLength, gapLength, offset);
    // Bottom edge
    this.drawDashedLine(g, x + width, y + height, x, y + height, color, lineWidth, alpha, dashLength, gapLength, offset);
    // Left edge
    this.drawDashedLine(g, x, y + height, x, y, color, lineWidth, alpha, dashLength, gapLength, offset);
  }

  /**
   * Draw a dashed line
   */
  private drawDashedLine(
    g: Graphics,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: number,
    lineWidth: number,
    alpha: number,
    dashLength: number,
    gapLength: number,
    offset: number
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);
    const unitX = dx / length;
    const unitY = dy / length;
    const totalLength = dashLength + gapLength;

    let pos = offset % totalLength;
    if (pos < 0) pos += totalLength;

    while (pos < length) {
      const dashStart = pos;
      const dashEnd = Math.min(pos + dashLength, length);

      if (dashEnd > 0 && dashStart < length) {
        const startX = x1 + unitX * Math.max(dashStart, 0);
        const startY = y1 + unitY * Math.max(dashStart, 0);
        const endX = x1 + unitX * dashEnd;
        const endY = y1 + unitY * dashEnd;

        g.moveTo(startX, startY);
        g.lineTo(endX, endY);
        g.stroke({ width: lineWidth, color, alpha });
      }

      pos += totalLength;
    }
  }

  /**
   * Draw hot zones
   */
  private drawHotZones(): void {
    const g = this.hotZones;
    g.clear();

    for (const hotZone of this.activeHotZones.values()) {
      const pos = this.worldToMinimap(hotZone.centerX, hotZone.centerY);

      // Check if within minimap bounds
      if (!this.isWithinMinimapBounds(pos.x, pos.y, 10)) continue;

      const configKey = hotZone.status as keyof typeof HOT_ZONE_CONFIGS;
      const config = HOT_ZONE_CONFIGS[configKey];
      if (!config) continue;

      // Zone indicator size based on status
      const baseRadius = this.config.mapShape === MapShape.RECTANGULAR
        ? Math.min(this.minimapWidth, this.minimapHeight) * 0.06
        : this.minimapRadius * 0.12;
      const zoneRadius = baseRadius * (1 + Object.keys(HOT_ZONE_CONFIGS).indexOf(hotZone.status) * 0.15);

      // Pulsing effect for hot zones
      const pulse = Math.sin(this.animTime * 4) * 0.3 + 0.7;

      // Draw zone glow
      g.circle(pos.x, pos.y, zoneRadius * 1.5);
      g.fill({ color: config.color, alpha: config.alpha * pulse * 0.5 });

      g.circle(pos.x, pos.y, zoneRadius);
      g.fill({ color: config.color, alpha: config.alpha * pulse });

      // Draw crown for jackpot zone
      if ('showCrown' in config && config.showCrown) {
        this.drawCrown(g, pos.x, pos.y - zoneRadius * 0.3, zoneRadius * 0.5);
      }

      // Draw "HOT!" text for hot zone
      if ('showText' in config && config.showText) {
        // Draw text indicator as small label
        g.rect(pos.x - 10, pos.y - zoneRadius - 8, 20, 8);
        g.fill({ color: 0xff0000, alpha: 0.8 * pulse });
      }
    }
  }

  /**
   * Draw a crown icon
   */
  private drawCrown(g: Graphics, cx: number, cy: number, size: number): void {
    const pulse = Math.sin(this.animTime * 6) * 0.2 + 0.9;

    // Crown shape
    g.moveTo(cx - size, cy + size * 0.3);
    g.lineTo(cx - size * 0.6, cy - size * 0.3);
    g.lineTo(cx - size * 0.3, cy);
    g.lineTo(cx, cy - size * 0.6);
    g.lineTo(cx + size * 0.3, cy);
    g.lineTo(cx + size * 0.6, cy - size * 0.3);
    g.lineTo(cx + size, cy + size * 0.3);
    g.closePath();
    g.fill({ color: 0xffd700, alpha: pulse });
    g.stroke({ width: 1, color: 0xffffff, alpha: pulse * 0.8 });
  }

  /**
   * Draw feast pings
   */
  private drawFeastPings(): void {
    const g = this.feastPings;
    g.clear();

    const currentTime = this.animTime;
    const pingsToRemove: string[] = [];

    for (const [id, ping] of this.activePings) {
      const age = currentTime - ping.spawnTime;
      if (age > ping.duration) {
        pingsToRemove.push(id);
        continue;
      }

      const pos = this.worldToMinimap(ping.x, ping.y);

      // Check if within minimap bounds, clamp to edge if outside
      if (!this.isWithinMinimapBounds(pos.x, pos.y, 5)) {
        // Clamp to edge
        if (this.config.mapShape === MapShape.RECTANGULAR) {
          const hw = this.minimapWidth / 2 - 5;
          const hh = this.minimapHeight / 2 - 5;
          pos.x = Math.max(-hw, Math.min(hw, pos.x));
          pos.y = Math.max(-hh, Math.min(hh, pos.y));
        } else {
          const dist = Math.sqrt(pos.x * pos.x + pos.y * pos.y);
          const scale = (this.minimapRadius - 5) / dist;
          pos.x *= scale;
          pos.y *= scale;
        }
      }

      // Skip if tier doesn't have a config (only FEAST, BANQUET, GOLDEN_BUFFET, MYTHICAL_FEAST have configs)
      if (!(ping.tier in FEAST_CONFIGS)) continue;
      const config = FEAST_CONFIGS[ping.tier as keyof typeof FEAST_CONFIGS];
      const fadeAlpha = Math.min(1, (ping.duration - age) / 2); // Fade out in last 2 seconds

      // Pulsing animation
      const pulse = Math.sin(this.animTime * config.pulseSpeed) * 0.3 + 0.7;
      const size = config.size * pulse;

      // Draw outer glow
      g.circle(pos.x, pos.y, size * 2);
      g.fill({ color: config.color, alpha: fadeAlpha * 0.2 * pulse });

      // Draw beacon
      g.circle(pos.x, pos.y, size * 1.3);
      g.fill({ color: config.color, alpha: fadeAlpha * 0.4 * pulse });

      // Draw core
      g.circle(pos.x, pos.y, size * 0.7);
      g.fill({ color: config.color, alpha: fadeAlpha * 0.9 });

      // Draw white center
      g.circle(pos.x, pos.y, size * 0.3);
      g.fill({ color: 0xffffff, alpha: fadeAlpha * 0.8 });

      // Draw crown for mythical feast
      if (ping.tier === FeastTier.MYTHICAL_FEAST) {
        this.drawCrown(g, pos.x, pos.y - size * 1.5, size * 0.8);
      }

      // Draw rays for higher tier feasts
      if (ping.tier === FeastTier.GOLDEN_BUFFET || ping.tier === FeastTier.MYTHICAL_FEAST) {
        const rayCount = ping.tier === FeastTier.MYTHICAL_FEAST ? 8 : 6;
        const rayLength = size * 2;
        for (let i = 0; i < rayCount; i++) {
          const angle = (i / rayCount) * Math.PI * 2 + this.animTime * 2;
          const rayX = pos.x + Math.cos(angle) * rayLength;
          const rayY = pos.y + Math.sin(angle) * rayLength;
          g.moveTo(pos.x + Math.cos(angle) * size, pos.y + Math.sin(angle) * size);
          g.lineTo(rayX, rayY);
          g.stroke({ width: 2, color: config.color, alpha: fadeAlpha * 0.5 * pulse });
        }
      }
    }

    // Remove expired pings
    for (const id of pingsToRemove) {
      this.activePings.delete(id);
    }
  }

  /**
   * Update ticker display
   */
  private updateTicker(): void {
    const currentTime = this.animTime;

    // Remove expired messages
    this.tickerMessages = this.tickerMessages.filter(
      msg => currentTime - msg.timestamp < msg.duration
    );

    // Show most recent message
    if (this.tickerMessages.length > 0) {
      const latestMsg = this.tickerMessages[this.tickerMessages.length - 1]!;
      const age = currentTime - latestMsg.timestamp;
      const fadeAlpha = Math.min(1, (latestMsg.duration - age) / 1.5);

      this.tickerText.text = latestMsg.text;
      this.tickerText.style.fill = latestMsg.color;
      this.tickerText.alpha = fadeAlpha;
      this.tickerBg.alpha = fadeAlpha * 0.85;
      this.tickerContainer.visible = true;
    } else {
      this.tickerContainer.visible = false;
    }
  }

  /**
   * Update minimap content
   * @param dt Delta time in seconds
   * @param food Food array (used for feast detection)
   * @param worms Array of worm data
   * @param localPlayerPos Local player position (if any)
   */
  update(
    dt: number,
    food: MinimapFood[],
    worms: MinimapWorm[],
    localPlayerPos?: { x: number; y: number }
  ): void {
    if (!this.visible) return;

    this.animTime += dt;

    // Update current zone
    if (localPlayerPos) {
      this.currentZone = this.getZoneName(localPlayerPos.x, localPlayerPos.y);
      this.zoneLabel.text = this.currentZone;
    }

    // Process food data for feasts and hot zones
    this.processFoodData(food);

    // Update border glow animation
    this.updateBorderGlow();

    // Draw shrinking zone (if enabled)
    this.drawShrinkingZone();

    // Draw hot zones
    this.drawHotZones();

    // Draw feast pings
    this.drawFeastPings();

    // Clear content
    const g = this.content;
    g.clear();

    // Draw worms (except local player) with glow effect
    // Draw LEVIATHAN last so it's always on top
    let leviathanWorm: MinimapWorm | null = null;

    for (const worm of worms) {
      if (worm.isLocal) continue;

      // Save LEVIATHAN for later (draw on top)
      if (worm.isLegendary) {
        leviathanWorm = worm;
        continue;
      }

      const pos = this.worldToMinimap(worm.x, worm.y);

      // Check if within minimap bounds
      if (!this.isWithinMinimapBounds(pos.x, pos.y, 4)) continue;

      // Draw glowing worm dot (arcade style)
      g.circle(pos.x, pos.y, 6);
      g.fill({ color: worm.color, alpha: 0.3 });
      g.circle(pos.x, pos.y, 4);
      g.fill({ color: worm.color, alpha: 0.8 });
      g.circle(pos.x, pos.y, 2);
      g.fill({ color: 0xffffff, alpha: 0.5 });
    }

    // Draw LEVIATHAN with special indicator (always visible, on top)
    if (leviathanWorm) {
      this.drawLeviathanMarker(leviathanWorm);
    }

    // Draw local player marker
    this.drawPlayerMarker(localPlayerPos);

    // Update ticker
    this.updateTicker();
  }

  /**
   * Draw LEVIATHAN marker on minimap
   * Special purple/gold pulsing indicator with crown icon
   */
  private drawLeviathanMarker(worm: MinimapWorm): void {
    const g = this.content;
    const pos = this.worldToMinimap(worm.x, worm.y);

    // Don't clamp LEVIATHAN to bounds - always show full marker
    // Just limit position to edge if outside
    let markerPos = { ...pos };
    if (!this.isWithinMinimapBounds(pos.x, pos.y, 10)) {
      if (this.config.mapShape === MapShape.RECTANGULAR) {
        const hw = this.minimapWidth / 2 - 10;
        const hh = this.minimapHeight / 2 - 10;
        markerPos.x = Math.max(-hw, Math.min(hw, pos.x));
        markerPos.y = Math.max(-hh, Math.min(hh, pos.y));
      } else {
        const dist = Math.sqrt(pos.x * pos.x + pos.y * pos.y);
        const scale = (this.minimapRadius - 10) / dist;
        markerPos.x *= scale;
        markerPos.y *= scale;
      }
    }

    // Pulsing animations
    const pulse = Math.sin(this.animTime * 3) * 0.3 + 0.7;
    const fastPulse = Math.sin(this.animTime * 6) * 0.2 + 0.8;

    // Large outer purple glow
    g.circle(markerPos.x, markerPos.y, 18);
    g.fill({ color: MINIMAP_COLORS.leviathanGlow, alpha: pulse * 0.25 });

    // Middle gold glow
    g.circle(markerPos.x, markerPos.y, 14);
    g.fill({ color: MINIMAP_COLORS.leviathanSecondary, alpha: pulse * 0.3 });

    // Inner purple glow
    g.circle(markerPos.x, markerPos.y, 10);
    g.fill({ color: MINIMAP_COLORS.leviathanPrimary, alpha: pulse * 0.6 });

    // Gold ring
    g.circle(markerPos.x, markerPos.y, 7);
    g.stroke({ width: 2, color: MINIMAP_COLORS.leviathanSecondary, alpha: 1 });

    // Purple core
    g.circle(markerPos.x, markerPos.y, 5);
    g.fill({ color: MINIMAP_COLORS.leviathanPrimary, alpha: 1 });

    // White hotspot
    g.circle(markerPos.x - 1, markerPos.y - 1, 1.5);
    g.fill({ color: 0xffffff, alpha: 0.8 });

    // Draw crown above marker
    this.drawCrown(g, markerPos.x, markerPos.y - 14, 6 * fastPulse);

    // Draw "LEVIATHAN" label if close enough to center (within 70% of minimap)
    // (Label drawing would require Text which we don't want to create each frame)
    // Instead, draw danger rays around LEVIATHAN
    const rayCount = 4;
    const rayLength = 12;
    for (let i = 0; i < rayCount; i++) {
      const angle = (i / rayCount) * Math.PI * 2 + this.animTime * 1.5;
      const innerRadius = 10;
      g.moveTo(
        markerPos.x + Math.cos(angle) * innerRadius,
        markerPos.y + Math.sin(angle) * innerRadius
      );
      g.lineTo(
        markerPos.x + Math.cos(angle) * (innerRadius + rayLength * pulse),
        markerPos.y + Math.sin(angle) * (innerRadius + rayLength * pulse)
      );
      g.stroke({ width: 2, color: MINIMAP_COLORS.leviathanSecondary, alpha: pulse * 0.7 });
    }
  }

  /**
   * Draw the local player marker with arcade hero styling
   */
  private drawPlayerMarker(pos?: { x: number; y: number }): void {
    const g = this.playerMarker;
    g.clear();

    if (!pos) return;

    const minimapPos = this.worldToMinimap(pos.x, pos.y);

    // Check if within bounds, clamp if necessary
    if (!this.isWithinMinimapBounds(minimapPos.x, minimapPos.y, 6)) {
      if (this.config.mapShape === MapShape.RECTANGULAR) {
        const hw = this.minimapWidth / 2 - 6;
        const hh = this.minimapHeight / 2 - 6;
        minimapPos.x = Math.max(-hw, Math.min(hw, minimapPos.x));
        minimapPos.y = Math.max(-hh, Math.min(hh, minimapPos.y));
      } else {
        const dist = Math.sqrt(minimapPos.x * minimapPos.x + minimapPos.y * minimapPos.y);
        const scale = (this.minimapRadius - 6) / dist;
        minimapPos.x *= scale;
        minimapPos.y *= scale;
      }
    }

    // Pulsing animation
    const pulse = Math.sin(this.animTime * 4) * 0.3 + 0.7;
    const fastPulse = Math.sin(this.animTime * 8) * 0.2 + 0.8;

    // Outer glow ring (arcade hero effect)
    g.circle(minimapPos.x, minimapPos.y, 14);
    g.fill({ color: MINIMAP_COLORS.playerGlow, alpha: pulse * 0.2 });

    // Middle glow
    g.circle(minimapPos.x, minimapPos.y, 10);
    g.fill({ color: MINIMAP_COLORS.playerGlow, alpha: pulse * 0.4 });

    // White border ring
    g.circle(minimapPos.x, minimapPos.y, 7);
    g.stroke({ width: 2, color: 0xffffff, alpha: 1 });

    // Golden center
    g.circle(minimapPos.x, minimapPos.y, 5);
    g.fill({ color: MINIMAP_COLORS.playerGlow, alpha: 1 });

    // Bright yellow core
    g.circle(minimapPos.x, minimapPos.y, 3);
    g.fill({ color: MINIMAP_COLORS.playerCore, alpha: fastPulse });

    // Tiny white hotspot
    g.circle(minimapPos.x - 1, minimapPos.y - 1, 1.5);
    g.fill({ color: 0xffffff, alpha: 0.9 });
  }

  /**
   * Set a new world radius for scaling
   */
  setWorldRadius(radius: number): void {
    this.config.worldRadius = radius;
  }

  /**
   * Manually trigger a feast ping (for external events like death drops)
   */
  triggerFeastPing(x: number, y: number, value: number): void {
    this.addFeastPing(x, y, value);
  }

  /**
   * Get the current player zone name
   */
  getCurrentZone(): string {
    return this.currentZone;
  }

  /**
   * Get the minimap's bounding box (for layout purposes)
   */
  getBounds(): { x: number; y: number; width: number; height: number } {
    if (this.config.mapShape === MapShape.RECTANGULAR) {
      return {
        x: this.container.x - this.minimapWidth / 2,
        y: this.container.y - this.minimapHeight / 2,
        width: this.minimapWidth,
        height: this.minimapHeight,
      };
    }
    return {
      x: this.container.x - this.minimapRadius,
      y: this.container.y - this.minimapRadius,
      width: this.minimapRadius * 2,
      height: this.minimapRadius * 2,
    };
  }

  /**
   * Get bottom Y position (for positioning other elements below)
   */
  getBottomY(): number {
    // Account for ticker height
    if (this.config.mapShape === MapShape.RECTANGULAR) {
      return this.container.y + this.minimapHeight / 2 + 30;
    }
    return this.container.y + this.minimapRadius + 30;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.background.destroy();
    this.border.destroy();
    this.borderGlow.destroy();
    this.shrinkingZoneGraphics.destroy();
    this.safeZoneBorder.destroy();
    this.hotZones.destroy();
    this.feastPings.destroy();
    this.content.destroy();
    this.playerMarker.destroy();
    this.zoneLabel.destroy();
    this.tickerBg.destroy();
    this.tickerText.destroy();
    this.tickerContainer.destroy({ children: true });
    this.container.destroy({ children: true });
    this.activePings.clear();
    this.activeHotZones.clear();
    this.tickerMessages = [];
    // Clean up DOM minimize button
    if (this.minimizeBtn) {
      this.minimizeBtn.remove();
      this.minimizeBtn = null;
    }
  }
}
